import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../../../context/UserContext';
import { useAuth } from '../../../hook/userRoleHook';
import { ROLES } from '../../../constants/general.constant';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getClosingAlerts, updateLoanClosing } from '../../../services/customer.service';
import { Deal } from '../../../validation/customer.validation';
import { formatIsoDateToDisplay } from '../../../helper/dateFormatter.helper';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parse } from "date-fns";

const todayValue = () => new Date().toISOString().slice(0, 10);

const getLeadName = (deal: Deal | null) => {
  const customer: any = deal?.customer;
  const customerName = customer
    ? `${customer.firstName || ''} ${customer.middleName || ''} ${customer.lastName || ''}`.trim()
    : '';
  return customerName || deal?.loanName || 'Selected lead';
};

const ClosingDealAlert: React.FC = () => {
  const { user } = useUser();
  const { role, loading } = useAuth();
  const userRole = !loading && !!user && (role === ROLES.BUSINESS || role === ROLES.CO_OWNER || role === ROLES.EMPLOYEE);
  const [alerts, setAlerts] = useState<Deal[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(todayValue());
  const [saving, setSaving] = useState(false);
  const activeDeal = alerts[0] || null;
  const leadName = useMemo(() => getLeadName(activeDeal), [activeDeal]);

  useEffect(() => {
    if (!userRole) return;

    const loadAlerts = async () => {
      try {
        const response = await getClosingAlerts();
        setAlerts(Array.isArray(response) ? response : []);
      } catch {
        setAlerts([]);
      }
    };

    loadAlerts();
  }, [userRole]);

  useEffect(() => {
    setShowOptions(false);
    setRescheduleDate(activeDeal?.closingDate ? activeDeal.closingDate.slice(0, 10) : todayValue());
  }, [activeDeal?.id, activeDeal?.closingDate]);

  const dismissActiveDeal = () => {
    setAlerts((prev) => prev.slice(1));
    setShowOptions(false);
  };

  const saveAlertAction = async (payload: { status?: string; closingDate?: string | null }) => {
    if (!activeDeal?.id) return;

    try {
      setSaving(true);
      await updateLoanClosing(activeDeal.id, payload);
      dismissActiveDeal();
    } finally {
      setSaving(false);
    }
  };

  if (loading || !userRole) return null;
  if (!activeDeal) return null;

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          mx: { xs: 2, sm: 0 },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <EventAvailableIcon />
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 18, sm: 20 },
            lineHeight: 1.2,
          }}
        >
          Closing Deal Alert
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            bgcolor: (theme) => theme.palette.action.hover,
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            Deal
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontWeight: 800,
              color: "text.primary",
              wordBreak: "break-word",
            }}
          >
            {leadName}
          </Typography>
          <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 14 }}>
            Closing Date:{" "}
            <Box
              component="span"
              sx={{ fontWeight: 800, color: "text.primary" }}
            >
              {formatIsoDateToDisplay(activeDeal.closingDate)}
            </Box>
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
          sx={{ mt: 2.25 }}
        >
          <HelpOutlineIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontWeight: 800, color: "text.primary" }}>
              Do you want to close this deal?
            </Typography>
            <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: 14 }}>
              Select yes to close it now. Select no to reschedule or mark it as
              lost.
            </Typography>
          </Box>
        </Stack>

        {showOptions && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Stack spacing={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Reschedule closing date"
                  value={
                    rescheduleDate
                      ? parse(rescheduleDate, "yyyy-MM-dd", new Date())
                      : null
                  }
                  onChange={(date) =>
                    setRescheduleDate(date ? format(date, "yyyy-MM-dd") : "")
                  }
                  format="MM-dd-yyyy"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      placeholder: "MM-DD-YYYY",
                      InputLabelProps: { shrink: true },
                      sx: { mt: 2.5 },
                      InputProps: {
                        sx: {
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderRadius: "10px",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                sx={{ "& .MuiButton-root": { flex: 1 } }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} />
                    ) : (
                      <EventRepeatIcon />
                    )
                  }
                  onClick={() =>
                    saveAlertAction({ closingDate: rescheduleDate })
                  }
                  disabled={saving || !rescheduleDate}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Save Date
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <HighlightOffIcon />
                    )
                  }
                  onClick={() =>
                    saveAlertAction({ status: "Lost Deal", closingDate: null })
                  }
                  disabled={saving}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Lost Deal
                </Button>
              </Stack>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1,
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
            borderRadius: 2,
            fontWeight: 700,
          },
        }}
      >
        {!showOptions && (
          <>
            <Button
              onClick={() => setShowOptions(true)}
              disabled={saving}
              variant="outlined"
              color="primary"
              startIcon={<EventRepeatIcon />}
            >
              No
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={() =>
                saveAlertAction({ status: "Closed", closingDate: todayValue() })
              }
              disabled={saving}
            >
              Yes
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClosingDealAlert;

